import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";
import { SeverityDot } from "@/shared/ui/severity-dot";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import { formatPickRate } from "../../format/formatPickRate";
import type { AnalysisPurchase } from "../../model/Analysis";
import { PURCHASE_VERDICT_LABELS } from "../../model/PURCHASE_VERDICT_LABELS";
import { PURCHASE_VERDICT_TONES } from "../../model/PURCHASE_VERDICT_TONES";
import { AssetIcon } from "../AssetIcon";
import { SLOT_COLORS } from "./SLOT_COLORS";

const PICK = msg({ id: "analysis.buy.pick", message: "taken by {pickRate}" });
const NOT_TAKEN = msg({ id: "analysis.buy.not_taken", message: "not taken" });
const TIP = msg({
  id: "analysis.buy.tip",
  message: "{item} · usually {baseline} · {pickRate} of strong {hero} players take it",
});
const TIP_NOT_TAKEN = msg({
  id: "analysis.buy.tip_not_taken",
  message: "{item} · strong {hero} players don't take it",
});

type PurchaseTileProps = {
  purchase: AnalysisPurchase;
  heroId: number;
};

// Предмет на таймлайне закупа: иконка в рамке цвета ветки, отметка оценки и время покупки,
// под ним — сколько сильных игроков его берут: слово «редкий» без цифры читалось как ошибка,
// а подсказку при наведении на телефоне не открыть. Оценка ключевого предмета — только
// цветом отметки: опоздание считается по душам к покупке, и разница в минутах ей бы спорила.
// Подробности — во всплывающей подсказке.
export const PurchaseTile = ({ purchase, heroId }: PurchaseTileProps) => {
  const { _, i18n } = useLingui();
  const item = useAssetLookup("items")(purchase.item_id);
  const hero = useAssetLookup("heroes")(heroId);
  const tone = PURCHASE_VERDICT_TONES[purchase.verdict];
  const { baseline_at_s: baseline, pick_rate: pickRate } = purchase;
  const note =
    pickRate === null
      ? null
      : pickRate === 0
        ? _(NOT_TAKEN)
        : _({ ...PICK, values: { pickRate: formatPickRate(pickRate, i18n.locale) } });
  const tip =
    baseline !== null && pickRate !== null
      ? _({
          ...TIP,
          values: {
            item: item.name,
            baseline: formatGameTime(baseline),
            pickRate: formatPickRate(pickRate, i18n.locale),
            hero: hero.name,
          },
        })
      : pickRate === 0
        ? _({ ...TIP_NOT_TAKEN, values: { item: item.name, hero: hero.name } })
        : item.name;
  return (
    <li
      title={tip}
      data-item-id={purchase.item_id}
      data-verdict={purchase.verdict}
      className="flex w-[70px] flex-col items-center gap-0.5"
    >
      <span className="relative block">
        <AssetIcon
          asset={item}
          size={60}
          className={cn(
            "rounded-[10px] border-[3px]",
            item.slot === null ? "border-border" : SLOT_COLORS[item.slot].border,
            purchase.verdict === "very_late" && "ring-2 ring-bad",
          )}
        />
        <SeverityDot
          severity={tone}
          className="absolute -top-1.5 -right-1.5 size-5 border-2 border-surface text-[11px]"
        />
      </span>
      <span className="sr-only">{_(PURCHASE_VERDICT_LABELS[purchase.verdict])}</span>
      <span className="font-mono text-xs">{formatGameTime(purchase.bought_at_s)}</span>
      {note !== null && (
        <span data-note="pick" className="text-[11px] whitespace-nowrap text-muted">
          {note}
        </span>
      )}
    </li>
  );
};
